/**
 * EnrichmentSessionPanel - Batch session management for Browserless enrichment
 * 
 * This component provides:
 * - Session start with LiveURL display
 * - Login verification status
 * - Redis cookie status check (v3.4)
 * - Session stats and controls
 * - End session button
 * 
 * Flow:
 * 1. User clicks "Start Session"
 * 2. LiveURL is displayed for Google OAuth
 * 3. User completes login and closes browser tab
 * 4. User clicks "Verify Login"
 * 5. Session is ready for batch enrichment
 * 6. User enriches multiple leads
 * 7. User clicks "End Session" when done
 * 
 * v3.4 Enhancement:
 * - When no active session, checks Redis for saved cookies
 * - Shows "Cookies available (X min)" if cookies are valid
 * - Provides clear guidance on whether to start a session or proceed with enrichment
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Divider,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudQueue as CloudIcon,
  Cookie as CookieIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { enrichmentSessionAPI } from '../../services/api';

// Session status colors
const STATUS_COLORS = {
  created: 'default',
  connecting: 'info',
  awaiting_login: 'warning',
  ready: 'success',
  scraping: 'info',
  error: 'error',
  closed: 'default',
};

// Session status labels
const STATUS_LABELS = {
  created: 'Created',
  connecting: 'Connecting...',
  awaiting_login: 'Awaiting Login',
  ready: 'Ready',
  scraping: 'Scraping',
  error: 'Error',
  closed: 'Closed',
};

/**
 * Format remaining time for LiveURL expiration
 */
function formatTimeRemaining(expiresAt) {
  if (!expiresAt) return null;
  
  const expiry = new Date(expiresAt);
  const now = new Date();
  const diffMs = expiry - now;
  
  if (diffMs <= 0) return 'Expired';
  
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

/**
 * EnrichmentSessionPanel Component
 */
export default function EnrichmentSessionPanel({ 
  jobId, 
  onSessionReady,
  onSessionEnded,
  compact = false,
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // Query for existing session
  const { 
    data: session, 
    isLoading: sessionLoading,
    refetch: refetchSession,
  } = useQuery(
    ['enrichmentSession', jobId],
    () => enrichmentSessionAPI.getJobSession(jobId),
    {
      staleTime: 5000,
      refetchInterval: (data) => {
        // Poll more frequently when awaiting login
        if (data?.status === 'awaiting_login') return 3000;
        if (data?.status === 'ready' || data?.status === 'scraping') return 10000;
        return false;
      },
    }
  );
  
  // Query for Redis cookies (v3.4) - always check, not just when dialog opens
  const { 
    data: redisCookies, 
    isLoading: cookiesLoading,
  } = useQuery(
    ['redisCookies', jobId],
    () => enrichmentSessionAPI.checkCookies(jobId),
    {
      staleTime: 30000, // Cache for 30 seconds
      retry: false,
    }
  );
  
  // Determine if we have valid Redis cookies
  const hasValidCookies = redisCookies?.valid === true;
  const cookieMinutesRemaining = redisCookies?.expires_in_minutes;
  
  // Start session mutation
  const startMutation = useMutation(
    () => enrichmentSessionAPI.startSession(jobId, 5), // 5 min timeout
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['enrichmentSession', jobId], data);
      },
    }
  );
  
  // Verify login mutation
  const verifyMutation = useMutation(
    (sessionId) => enrichmentSessionAPI.verifyLogin(sessionId),
    {
      onSuccess: (data) => {
        if (data.logged_in) {
          refetchSession();
          onSessionReady?.();
        }
      },
    }
  );
  
  // End session mutation
  const endMutation = useMutation(
    (sessionId) => enrichmentSessionAPI.endSession(sessionId),
    {
      onSuccess: () => {
        queryClient.setQueryData(['enrichmentSession', jobId], null);
        onSessionEnded?.();
      },
    }
  );
  
  // Update countdown timer for LiveURL expiration
  useEffect(() => {
    if (!session?.live_url_expires_at || session.logged_in) {
      setTimeRemaining(null);
      return;
    }
    
    const interval = setInterval(() => {
      setTimeRemaining(formatTimeRemaining(session.live_url_expires_at));
    }, 1000);
    
    // Initial update
    setTimeRemaining(formatTimeRemaining(session.live_url_expires_at));
    
    return () => clearInterval(interval);
  }, [session?.live_url_expires_at, session?.logged_in]);
  
  // Notify parent when session is ready
  useEffect(() => {
    if (session?.logged_in && session?.status === 'ready') {
      onSessionReady?.();
    }
  }, [session?.logged_in, session?.status, onSessionReady]);
  
  // Handle start session
  const handleStartSession = useCallback(() => {
    startMutation.mutate();
  }, [startMutation]);
  
  // Handle verify login
  const handleVerifyLogin = useCallback(() => {
    if (session?.session_id) {
      verifyMutation.mutate(session.session_id);
    }
  }, [session?.session_id, verifyMutation]);
  
  // Handle end session
  const handleEndSession = useCallback(() => {
    if (session?.session_id) {
      endMutation.mutate(session.session_id);
    }
  }, [session?.session_id, endMutation]);
  
  // Loading state
  if (sessionLoading || cookiesLoading) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography>Checking authentication status...</Typography>
        </Box>
      </Paper>
    );
  }
  
  // No active session - show cookie status and/or start button
  if (!session) {
    // Still loading cookie status
    if (cookiesLoading) {
      return (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>Checking authentication status...</Typography>
          </Box>
        </Paper>
      );
    }
    
    // Has valid cookies - show cookie status with option to start fresh session
    if (hasValidCookies) {
      return (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.lighter' }}>
          {/* Cookie Status Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CookieIcon color="success" />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    RecordOwl Authentication
                  </Typography>
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label="Ready"
                    color="success"
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Saved cookies available ({cookieMinutesRemaining} min remaining)
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Guidance Alert */}
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" fontWeight="bold">
              ✓ Ready to enrich leads!
            </Typography>
            <Typography variant="body2">
              Your previous login session saved cookies that are still valid. 
              Select a lead and click "Enrich" to proceed.
            </Typography>
          </Alert>
          
          {/* Optional: Start New Session */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'success.light' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Need a fresh session? Start a new one below.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={startMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : <StartIcon />}
              onClick={handleStartSession}
              disabled={startMutation.isLoading}
            >
              {startMutation.isLoading ? 'Starting...' : 'Start New Session'}
            </Button>
          </Box>
          
          {startMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to start session: {startMutation.error?.response?.data?.detail?.message || startMutation.error?.message}
            </Alert>
          )}
        </Paper>
      );
    }
    
    // No cookies - show start session prompt
    return (
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'warning.lighter' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudIcon color="warning" />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                RecordOwl Authentication Required
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No active session or saved cookies available
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={startMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <StartIcon />}
            onClick={handleStartSession}
            disabled={startMutation.isLoading}
          >
            {startMutation.isLoading ? 'Starting...' : 'Start Session'}
          </Button>
        </Box>
        
        {/* Guidance */}
        <Alert severity="info" icon={<InfoIcon />}>
          <Typography variant="body2">
            <strong>To enrich leads with RecordOwl data:</strong> Start a Browserless session and complete Google OAuth login. 
            Cookies will be saved for ~2 hours, allowing multiple enrichments without re-login.
          </Typography>
        </Alert>
        
        {startMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to start session: {startMutation.error?.response?.data?.detail?.message || startMutation.error?.message}
          </Alert>
        )}
      </Paper>
    );
  }
  
  // Active session - show status and controls
  const isAwaitingLogin = session.status === 'awaiting_login' && !session.logged_in;
  const isReady = session.logged_in && session.status === 'ready';
  const isError = session.status === 'error';
  
  return (
    <Paper sx={{ mb: 2, overflow: 'hidden' }}>
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          bgcolor: isReady ? 'success.lighter' : isError ? 'error.lighter' : 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CloudIcon color={isReady ? 'success' : isError ? 'error' : 'action'} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Browserless Session
              </Typography>
              <Chip 
                label={STATUS_LABELS[session.status] || session.status}
                color={STATUS_COLORS[session.status] || 'default'}
                size="small"
              />
              {session.logged_in && (
                <Chip 
                  icon={<CheckCircleIcon />}
                  label="Logged In"
                  color="success"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Session: {session.session_id} | Pages: {session.pages_scraped} | 
              {session.total_characters > 0 ? ` ${(session.total_characters / 1000).toFixed(1)}k chars` : ' 0 chars'}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isReady && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<StopIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleEndSession();
              }}
              disabled={endMutation.isLoading}
            >
              End Session
            </Button>
          )}
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      
      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {/* Error state */}
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {session.error_message || 'Session error occurred'}
              <Button 
                size="small" 
                onClick={handleEndSession}
                sx={{ ml: 2 }}
              >
                Close & Retry
              </Button>
            </Alert>
          )}
          
          {/* Awaiting login - show LiveURL */}
          {isAwaitingLogin && session.live_url && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Complete Google OAuth Login
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Click the button below to open the verification page. Complete these steps IN ORDER:
                </Typography>
                <Box component="ol" sx={{ pl: 2, mb: 2 }}>
                  <li>Click <strong>"Consent"</strong> on the cookie popup (if shown)</li>
                  <li>Click the user icon (top right) or navigate to Login</li>
                  <li>Click <strong>"Sign in with Google"</strong></li>
                  <li>Complete Google OAuth (email, password, 2FA if prompted)</li>
                  <li><strong>IMPORTANT:</strong> Wait until you see the RecordOwl page (NOT Google)</li>
                  <li>Come back here and click <strong>"Verify Login"</strong></li>
                  <li>Once verified, you can close the browser tab</li>
                </Box>
              </Alert>
              
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<OpenInNewIcon />}
                  href={session.live_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open LiveURL
                </Button>
                
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={verifyMutation.isLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                  onClick={handleVerifyLogin}
                  disabled={verifyMutation.isLoading}
                >
                  {verifyMutation.isLoading ? 'Verifying...' : 'Verify Login'}
                </Button>
                
                {timeRemaining && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TimeIcon fontSize="small" color={timeRemaining === 'Expired' ? 'error' : 'action'} />
                    <Typography 
                      variant="body2" 
                      color={timeRemaining === 'Expired' ? 'error' : 'text.secondary'}
                    >
                      {timeRemaining === 'Expired' ? 'LiveURL Expired' : `Expires in ${timeRemaining}`}
                    </Typography>
                  </Box>
                )}
              </Stack>
              
              {verifyMutation.isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Verification failed: {verifyMutation.error?.response?.data?.message || 'Unknown error'}
                </Alert>
              )}
              
              {verifyMutation.data && !verifyMutation.data.logged_in && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {verifyMutation.data.message}
                </Alert>
              )}
              
              {timeRemaining === 'Expired' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  LiveURL has expired. Please end this session and start a new one.
                  <Button 
                    size="small" 
                    onClick={handleEndSession}
                    sx={{ ml: 2 }}
                  >
                    End Session
                  </Button>
                </Alert>
              )}
            </Box>
          )}
          
          {/* Ready state - show session info */}
          {isReady && (
            <Box>
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Session is ready for enrichment!
                </Typography>
                <Typography variant="body2">
                  You can now enrich multiple leads without re-authenticating.
                </Typography>
              </Alert>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Session Statistics
              </Typography>
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="h4" color="primary">
                    {session.pages_scraped}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pages Scraped
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="primary">
                    {(session.total_characters / 1000).toFixed(1)}k
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Characters
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
